"use client";

/**
 * Quote calculation for roadside jobs.
 *
 * Timing is charged as a fixed, published allowance rather than a multiplier, so a late-night
 * breakdown never turns into open-ended surge pricing. The total is also capped relative to the
 * partner's base price.
 */

export type TimeSlotId =
  | "late_night"
  | "early_morning"
  | "peak_morning"
  | "peak_evening"
  | "late_evening"
  | "standard";

export type TimeSlot = {
  id: TimeSlotId;
  label: string;
  /** Fixed rupee allowance added on top of the base price. */
  allowance: number;
  /** Inclusive start hour, exclusive end hour on a 24h clock. Wraps past midnight when start > end. */
  startHour: number;
  endHour: number;
  note: string;
};

export const TIME_SLOTS: TimeSlot[] = [
  { id: "late_night", label: "Late night", allowance: 150, startHour: 23, endHour: 5, note: "11 PM – 5 AM night allowance" },
  { id: "early_morning", label: "Early morning", allowance: 60, startHour: 5, endHour: 8, note: "5 AM – 8 AM early call-out" },
  { id: "peak_morning", label: "Morning peak", allowance: 50, startHour: 8, endHour: 11, note: "8 AM – 11 AM office rush" },
  { id: "peak_evening", label: "Evening peak", allowance: 70, startHour: 17, endHour: 21, note: "5 PM – 9 PM traffic peak" },
  { id: "late_evening", label: "Late evening", allowance: 90, startHour: 21, endHour: 23, note: "9 PM – 11 PM late evening" },
  // Keep this last: it doubles as the fallback slot.
  { id: "standard", label: "Standard hours", allowance: 0, startHour: 11, endHour: 17, note: "No timing allowance" },
];

const STANDARD_SLOT = TIME_SLOTS[TIME_SLOTS.length - 1];

/** Kilometres included in the base price before travel is charged. */
export const FREE_DISTANCE_KM = 3;
export const PER_KM_RATE = 12;
/** Keeps a long, late-night job from ballooning past a predictable ceiling. */
export const MAX_TOTAL_MULTIPLE = 2.5;
export const MIN_TOTAL = 49;

function hourMatches(slot: TimeSlot, hour: number) {
  return slot.startHour <= slot.endHour
    ? hour >= slot.startHour && hour < slot.endHour
    : hour >= slot.startHour || hour < slot.endHour;
}

export function timeSlotFor(at: Date = new Date()) {
  return TIME_SLOTS.find((slot) => hourMatches(slot, at.getHours())) || STANDARD_SLOT;
}

export function slotById(id: TimeSlotId) {
  return TIME_SLOTS.find((slot) => slot.id === id) || STANDARD_SLOT;
}

export type QuoteBreakdown = {
  base: number;
  distanceCharge: number;
  timingCharge: number;
  total: number;
  chargeableKm: number;
  slot: TimeSlot;
  /** True when the cap trimmed the total below base + distance + timing. */
  capped: boolean;
};

export function suggestQuote(input: {
  basePrice: number;
  distanceKm?: number;
  at?: Date;
  slotId?: TimeSlotId;
}): QuoteBreakdown {
  const base = Math.max(MIN_TOTAL, Math.round(input.basePrice) || 299);
  const slot = input.slotId ? slotById(input.slotId) : timeSlotFor(input.at);
  const distanceKm = Number.isFinite(input.distanceKm) ? Math.max(0, Number(input.distanceKm)) : 0;
  const chargeableKm = Math.max(0, distanceKm - FREE_DISTANCE_KM);
  const distanceCharge = Math.round(chargeableKm * PER_KM_RATE);
  const timingCharge = slot.allowance;

  const raw = base + distanceCharge + timingCharge;
  const ceiling = Math.round(base * MAX_TOTAL_MULTIPLE);
  const capped = raw > ceiling;
  // Round to the nearest ₹10 so partners quote clean amounts.
  const total = Math.max(MIN_TOTAL, Math.round(Math.min(raw, ceiling) / 10) * 10);

  return {
    base,
    distanceCharge,
    timingCharge,
    total,
    chargeableKm: Math.round(chargeableKm * 10) / 10,
    slot,
    capped,
  };
}

/** Short human-readable maths for the UI, e.g. "₹299 base + 2.2 km ₹26 + Late night ₹150". */
export function explainQuote(breakdown: QuoteBreakdown) {
  const parts = [`₹${breakdown.base} base`];
  if (breakdown.distanceCharge > 0) parts.push(`${breakdown.chargeableKm} km ₹${breakdown.distanceCharge}`);
  if (breakdown.timingCharge > 0) parts.push(`${breakdown.slot.label} ₹${breakdown.timingCharge}`);
  const line = parts.join(" + ");
  return breakdown.capped ? `${line} (capped at ₹${breakdown.total})` : line;
}
